import React, { useState, useRef } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FiPaperclip, FiDownload, FiTrash2 } from 'react-icons/fi';

const MAX_MB = 12;
const MAX_BYTES = MAX_MB * 1024 * 1024;
const MAX_FILES = 12;

const ACCEPT = [
  'image/*',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
].join(',');

const HRAttachmentField = ({ attachments = [], onUpload, onDelete, onDownload, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (files.length + attachments.length > MAX_FILES) {
      setError(`You can upload up to ${MAX_FILES} files per item.`);
      e.target.value = '';
      return;
    }

    const oversized = files.find((file) => file.size > MAX_BYTES);
    if (oversized) {
      setError(`File too large. Max ${MAX_MB} MB allowed per file.`);
      e.target.value = '';
      return;
    }

    setError('');
    setUploading(true);
    try {
      await onUpload(files);
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : err?.message || err?.data?.message || 'Upload failed. Please try again.';
      setError(message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (index) => {
    setDeletingIndex(index);
    setError('');
    try {
      await onDelete(index);
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : err?.message || err?.data?.message || 'Delete failed.';
      setError(message);
    } finally {
      setDeletingIndex(null);
    }
  };

  return (
    <div className="mb-3">
      <strong style={{ fontSize: 13 }}>Attachments: </strong>
      <div className="mt-1">
        {attachments.length > 0 && (
          <div className="d-flex flex-column gap-2 mb-2">
            {attachments.map((attachment, index) => (
              <div key={`${attachment.name || attachment.path}-${index}`} className="d-flex align-items-center gap-2 flex-wrap">
                <span style={{ fontSize: 13 }} className="text-truncate" title={attachment.name || attachment.path}>
                  <FiPaperclip size={13} className="me-1" />
                  {attachment.name || attachment.path}
                </span>
                <Button variant="outline-primary" size="sm" className="py-0 px-2" onClick={() => onDownload(index)} title="Download">
                  <FiDownload size={13} />
                </Button>
                {!disabled && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="py-0 px-2"
                    onClick={() => handleDelete(index)}
                    disabled={deletingIndex === index}
                    title="Remove"
                  >
                    {deletingIndex === index ? <Spinner animation="border" size="sm" /> : <FiTrash2 size={13} />}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {!disabled && attachments.length < MAX_FILES && (
          <Button variant="outline-secondary" size="sm" className="py-0 px-2" onClick={() => inputRef.current?.click()} disabled={uploading}>
            {uploading ? <><Spinner animation="border" size="sm" className="me-1" />Uploading...</> : <><FiPaperclip size={13} className="me-1" />Attach file(s)</>}
          </Button>
        )}

        {uploading && attachments.length > 0 && <Spinner animation="border" size="sm" className="ms-2" />}

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>
      {error && <div style={{ fontSize: 12, color: 'var(--bs-danger)', marginTop: 4 }}>{error}</div>}
      <div style={{ fontSize: 11, color: 'var(--bs-secondary)', marginTop: 2 }}>
        Up to {MAX_FILES} files, max {MAX_MB} MB each · Images, PDF, Word, Excel, PPT, CSV
      </div>
    </div>
  );
};

export default HRAttachmentField;
